"use client";

import styles from "./heroGradientTextWithIcon.module.scss";
import { useEffect, useRef, useCallback } from "react";
import dynamic from "next/dynamic";
import SplitType from "split-type";
import { gsap } from "gsap";
import RoundedButton from "../roundedButton/roundedButton";
import Image from "next/image";
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

function HeroGradientTextWithIcon({
  title,
  subtitle,
  description,
  image,
  buttonTitle,
  buttonUrl,
  target = "",
  isProduct,
  isSolution,
  isStatic,
  isPricing = false,
}) {
  const heroRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const splitInstance = useRef<SplitType | null>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);
  const isInitialMount = useRef(true);

  // Memoize animation setup to prevent unnecessary re-runs
  const setupAnimation = useCallback(() => {
    if (!subtitleRef.current || !imageRef.current) return;

    // Kill any existing animations and cleanup
    if (timeline.current) {
      timeline.current.kill();
    }
    if (splitInstance.current) {
      splitInstance.current.revert();
    }

    // Reset visibility
    if (contentRef.current) {
      gsap.set(contentRef.current, { visibility: "visible" });
    }

    // Initialize SplitType for subtitle only if needed
    if (subtitleRef.current && !splitInstance.current) {
      splitInstance.current = new SplitType(subtitleRef.current, {
        types: "words",
      });
    }

    // Create main timeline with reduced duration
    timeline.current = gsap.timeline({
      defaults: {
        ease: "power2.out",
        duration: 1.5,
      },
    });

    // Build the animation sequence with optimized timing
    if (splitInstance.current?.words && timeline.current) {
      timeline.current
        .fromTo(
          splitInstance.current.words,
          {
            opacity: 0.3,
          },
          {
            opacity: 1,
            duration: 0.5, // Reduced from 0.8
            stagger: 0.1, // Reduced from 0.2
            ease: "power2.out",
          }
        )
        .fromTo(
          imageRef.current,
          {
            opacity: 0,
            scale: 0.9, // Less dramatic scale
          },
          {
            opacity: 1,
            scale: 1,
            duration: 0.5, // Reduced from 0.8
            ease: "power2.out",
          },
          "-=0.3" // Reduced overlap
        );
    }
  }, []); // Empty dependency array since we're using refs

  useEffect(() => {
    // Only run animation on initial mount
    if (isInitialMount.current) {
      setupAnimation();
      isInitialMount.current = false;
    }

    // Cleanup function
    return () => {
      if (timeline.current) {
        timeline.current.kill();
      }
      if (splitInstance.current) {
        splitInstance.current.revert();
      }
      // Reset any GSAP-related styles
      const elements = [subtitleRef.current, imageRef.current].filter(Boolean);
      if (elements.length > 0) {
        gsap.set(elements, { clearProps: "all" });
      }
    };
  }, [setupAnimation]);

  return (
    <>
      <div
        className={`${styles["heroGradientTextWithIcon"]}  ${
          isProduct ? styles["isProduct"] : ""
        } ${isStatic ? styles["isStatic"] : ""} ${
          isSolution ? styles["isSolution"] : ""
        }
        ${isPricing ? styles["isPricing"] : ""}`}
        ref={heroRef}
      >
        <div className={`custom-container`}>
          <div className={`inner`}>
            <div
              className={`${styles["heroGradientTextWithIcon__content"]}
              ${
                isProduct
                  ? styles["column"]
                  : isStatic
                  ? styles["columnReverse"]
                  : isSolution
                  ? styles["column"]
                  : styles["columnReverse"]
              }
              `}
              ref={contentRef}
            >
              <div>
                <div
                  className={`${styles["heroGradientTextWithIcon__content-text"]}`}
                >
                  <h1 className="font-size-32">
                    <div className={`${styles.animation}`}>
                      <span className={`${styles.first} font-size-40`}>
                        {subtitle}
                      </span>
                      <span className={`${styles.oh}`}>
                        <span
                          className={`${styles.second} ${
                            isProduct
                              ? "font-size-120"
                              : isStatic
                              ? "font-size-120"
                              : isSolution
                              ? "font-size-95"
                              : "font-size-190"
                          } `}
                          dangerouslySetInnerHTML={{ __html: title }}
                        ></span>
                      </span>
                    </div>
                  </h1>
                  <p
                    className="font-size-24"
                    dangerouslySetInnerHTML={{ __html: description }}
                    ref={subtitleRef}
                  />
                  {buttonTitle && (
                    <div
                      className={`${styles["heroGradientTextWithIcon__button"]}`}
                    >
                      <RoundedButton
                        title={buttonTitle}
                        url={buttonUrl}
                        light={false}
                        fill={true}
                        target={target}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div
                className={`${styles["heroGradientTextWithIcon__image"]} ${
                  isProduct
                    ? `${styles["heroGradientTextWithIcon__image-product"]}`
                    : isSolution
                    ? `${styles["heroGradientTextWithIcon__image-solution"]}`
                    : ""
                } ${
                  !isProduct && !isSolution && !isStatic ? styles["rotate"] : ""
                }`}
                ref={imageRef}
                style={{
                  marginRight: isSolution ? "0px" : "100px",
                }}
              >
                <div className={styles.imageContainer}>{image}</div>
                {isSolution && (
                  <div className={styles.imageOverlay}>
                    <Image
                      src="/images/solution-bg.svg"
                      alt="solutions"
                      width={960}
                      height={720}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`${styles["heroGradientTextWithIcon__interactive-gradient-container"]}`}
        ></div>
      </div>
    </>
  );
}

export default HeroGradientTextWithIcon;
